import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type Theme = 'light' | 'dark';
export type ThemeMode = Theme | 'system';

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  themeMode: 'light',
  setThemeMode: () => {},
  toggleTheme: () => {},
  isDark: false,
});

const STORAGE_KEY = 'app-theme';

const getSystemTheme = (): Theme =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const getInitialThemeMode = (): ThemeMode => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light' || saved === 'system') {
      return saved;
    }
  } catch {
    // localStorage unavailable – fall through
  }
  return 'light';
};

const resolveTheme = (mode: ThemeMode): Theme =>
  mode === 'system' ? getSystemTheme() : mode;

/**
 * Applies the AppKit4 data-theme attribute and a CSS class to <body>.
 */
const applyTheme = (theme: Theme) => {
  const root = document.documentElement;
  const body = document.body;
  root.setAttribute('data-theme', theme === 'dark' ? 'orange-dark' : 'orange');
  body.setAttribute('data-theme', theme === 'dark' ? 'orange-dark' : 'orange');

  // rex-ai-sidebar reads data-mode="dark" on the body to enable its built-in dark styles
  if (theme === 'dark') {
    root.classList.add('dark-theme');
    root.classList.remove('light-theme');
    body.setAttribute('data-mode', 'dark');
    body.classList.add('dark-theme');
    body.classList.remove('light-theme');
  } else {
    root.classList.add('light-theme');
    root.classList.remove('dark-theme');
    body.removeAttribute('data-mode');
    body.classList.add('light-theme');
    body.classList.remove('dark-theme');
  }
};

if (typeof document !== 'undefined') {
  applyTheme(resolveTheme(getInitialThemeMode()));
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getInitialThemeMode);
  const [theme, setTheme] = useState<Theme>(() => resolveTheme(getInitialThemeMode()));

  useEffect(() => {
    setTheme(resolveTheme(themeMode));
  }, [themeMode]);

  // Apply theme on mount and whenever it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow OS color scheme when theme mode is System.
  useEffect(() => {
    if (themeMode !== 'system') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;

    const handler = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? 'dark' : 'light');
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeMode]);

  // Force the workspace brand primary color across AppKit4 components.
  // AND force correct dark-mode CSS variables for all AppKit4 components
  // Injected via <style> tag so it always loads AFTER all AppKit4 CSS
  useEffect(() => {
    const id = 'theme-overrides';
    let style = document.getElementById(id) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = id;
      document.head.appendChild(style);
    }

    if (theme === 'dark') {
      style.textContent = `
        :root, :host, body, body.dark-theme {
          --color-background-primary: #0046b8 !important;
          --color-background-primary-rgb: 0,70,184 !important;
          --color-background-primary-light: #297bff !important;
          --color-background-primary-light-rgb: 41,123,255 !important;
          --color-background-primary-code: #0046b8 !important;
          --color-background-primary-code-rgb: 0,70,184 !important;
          --color-background-container: #252525 !important;
          --color-background-container-rgb: 37,37,37 !important;
          --color-background-default: #191919 !important;
          --color-background-default-rgb: 25,25,25 !important;
          --color-background-hover: #303030 !important;
          --color-background-hover-rgb: 48,48,48 !important;
          --color-background-hover-selected: #3b3b3b !important;
          --color-background-hover-selected-rgb: 59,59,59 !important;
          --color-text-heading: #fff !important;
          --color-text-body: #d1d1d1 !important;
          --color-text-light: #979797 !important;
          --color-background-border: dimgray !important;
          --color-background-alt: #252525 !important;
          --color-background-alt-rgb: 37,37,37 !important;
        }
      `;
    } else {
      style.textContent = `
        :root, :host, body, body.light-theme {
          --color-background-primary: #0046b8 !important;
          --color-background-primary-rgb: 0,70,184 !important;
          --color-background-primary-light: #297bff !important;
          --color-background-primary-light-rgb: 41,123,255 !important;
          --color-background-primary-code: #0046b8 !important;
          --color-background-primary-code-rgb: 0,70,184 !important;
        }
      `;
    }
  }, [theme]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    setTheme(resolveTheme(mode));
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* noop */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeMode(theme === 'light' ? 'dark' : 'light');
  }, [setThemeMode, theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeMode,
        setThemeMode,
        toggleTheme,
        isDark: theme === 'dark',
      }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
